/**
 * Nostr service — relay operations using SimplePool from nostr-tools.
 * Each function creates a fresh pool and closes it when done.
 * All incoming events are verified before use.
 */
import { SimplePool } from 'nostr-tools/pool'
import { verifyEvent } from 'nostr-tools'
import type { Filter } from 'nostr-tools'
import type { NostrSession } from '@/types/nostr'
import { DEFAULT_RELAYS, sanitizeRelays, createSignedEvent } from '@/lib/nostr'

export { DEFAULT_RELAYS }

/** Minimal Nostr event shape returned by the relay */
export interface NostrEvent {
    id: string
    pubkey: string
    created_at: number
    kind: number
    tags: string[][]
    content: string
    sig: string
}

/** Parsed kind-0 (metadata) event content */
export interface NostrProfileEvent {
    pubkey: string
    name?: string
    display_name?: string
    about?: string
    picture?: string
    banner?: string
    website?: string
    nip05?: string
    lud16?: string
}

function resolveRelays(session?: NostrSession, relays?: string[]): string[] {
    const candidate = relays ?? session?.relays ?? DEFAULT_RELAYS
    const safe = sanitizeRelays(candidate)
    return safe.length > 0 ? safe : DEFAULT_RELAYS
}

/**
 * Fetch a kind-0 (metadata) event for a pubkey.
 * Returns null if not found or invalid.
 */
export async function fetchNostrProfile(
    pubkey: string,
    relays?: string[]
): Promise<NostrProfileEvent | null> {
    const pool = new SimplePool()
    const safeRelays = resolveRelays(undefined, relays)
    try {
        const event = await pool.get(safeRelays, { kinds: [0], authors: [pubkey] })
        if (!event) return null
        if (!verifyEvent(event as Parameters<typeof verifyEvent>[0])) {
            console.warn('[nostrService] fetchNostrProfile: invalid event signature, skipping')
            return null
        }
        try {
            const parsed = JSON.parse(event.content) as Record<string, string>
            return {
                pubkey: event.pubkey,
                name: parsed.name,
                display_name: parsed.display_name,
                about: parsed.about,
                picture: parsed.picture?.startsWith('https://') ? parsed.picture : undefined,
                website: parsed.website?.startsWith('https://') ? parsed.website : undefined,
                nip05: parsed.nip05,
                lud16: parsed.lud16,
            }
        } catch {
            return { pubkey: event.pubkey }
        }
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Fetch a single event by its hex ID.
 * Returns null if not found, invalid, or relay error.
 */
export async function fetchNostrEventById(
    id: string,
    relays?: string[]
): Promise<NostrEvent | null> {
    const pool = new SimplePool()
    const safeRelays = resolveRelays(undefined, relays)
    try {
        const event = await pool.get(safeRelays, { ids: [id] })
        if (!event) return null
        if (!verifyEvent(event as Parameters<typeof verifyEvent>[0])) {
            console.warn('[nostrService] fetchNostrEventById: invalid event signature, skipping')
            return null
        }
        return event as NostrEvent
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Fetch kind-1 text notes that tag a specific event ID (i.e. replies to that event).
 */
export async function fetchNostrReplies(eventId: string, relays?: string[]): Promise<NostrEvent[]> {
    const pool = new SimplePool()
    const safeRelays = resolveRelays(undefined, relays)
    try {
        const events = await pool.querySync(safeRelays, { kinds: [1], '#e': [eventId], limit: 50 })
        return events.filter(e =>
            verifyEvent(e as Parameters<typeof verifyEvent>[0])
        ) as NostrEvent[]
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Fetch a home timeline for the session:
 * 1. Fetch kind-3 (contact list) to get followed pubkeys.
 * 2. Fetch kind-1 (text notes) from those authors.
 */
export async function fetchNostrTimeline(
    session: NostrSession,
    options?: { limit?: number; until?: number }
): Promise<NostrEvent[]> {
    const safeRelays = resolveRelays(session)
    const pool = new SimplePool()
    const limit = options?.limit ?? 50

    try {
        // Step 1: get contact list (kind 3)
        const contactEvent = await pool.get(safeRelays, {
            kinds: [3],
            authors: [session.pubkey],
        })

        let follows: string[] = [session.pubkey]
        if (contactEvent && verifyEvent(contactEvent as Parameters<typeof verifyEvent>[0])) {
            const extraPubkeys = contactEvent.tags
                .filter((t: string[]) => t[0] === 'p' && t[1])
                .map((t: string[]) => t[1])
            follows = [...new Set([...follows, ...extraPubkeys])]
        }

        const MAX_FOLLOWS_FOR_TIMELINE = 500
        follows = follows.slice(0, MAX_FOLLOWS_FOR_TIMELINE)

        // Step 2: fetch text notes from followed authors
        const filter: Filter = {
            kinds: [1],
            authors: follows,
            limit,
            ...(options?.until !== undefined ? { until: options.until } : {}),
        }

        const events = await pool.querySync(safeRelays, filter)
        return events
            .filter(e => verifyEvent(e as Parameters<typeof verifyEvent>[0]))
            .sort((a, b) => b.created_at - a.created_at) as NostrEvent[]
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Publish a kind-7 reaction ('+') for an event.
 * Returns the published event ID.
 */
export async function likeNostrPost(session: NostrSession, event: NostrEvent): Promise<string> {
    const safeRelays = resolveRelays(session)
    const tags = [
        ['e', event.id],
        ['p', event.pubkey],
    ]
    const signed = await createSignedEvent(session, 7, '+', tags)

    const pool = new SimplePool()
    try {
        await Promise.any(pool.publish(safeRelays, signed))
        return signed.id
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Publish a kind-6 repost for an event.
 * Returns the published event ID.
 */
export async function repostNostrPost(session: NostrSession, event: NostrEvent): Promise<string> {
    const safeRelays = resolveRelays(session)
    const tags = [
        ['e', event.id, safeRelays[0] ?? ''],
        ['p', event.pubkey],
    ]
    const signed = await createSignedEvent(session, 6, JSON.stringify(event), tags)

    const pool = new SimplePool()
    try {
        await Promise.any(pool.publish(safeRelays, signed))
        return signed.id
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Publish a kind-5 (deletion) event for a given event ID.
 */
export async function publishDeleteEvent(session: NostrSession, eventId: string): Promise<void> {
    const safeRelays = resolveRelays(session)
    const tags = [['e', eventId]]
    const signed = await createSignedEvent(session, 5, '', tags)

    const pool = new SimplePool()
    try {
        await Promise.any(pool.publish(safeRelays, signed))
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Fetch kind-3 contact list for a session pubkey.
 * Returns an array of followed pubkeys.
 */
export async function fetchContactList(session: NostrSession): Promise<string[]> {
    const safeRelays = resolveRelays(session)
    const pool = new SimplePool()
    try {
        const event = await pool.get(safeRelays, {
            kinds: [3],
            authors: [session.pubkey],
        })
        if (!event || !verifyEvent(event as Parameters<typeof verifyEvent>[0])) return []
        return event.tags.filter((t: string[]) => t[0] === 'p' && t[1]).map((t: string[]) => t[1])
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Publish an updated kind-3 contact list.
 */
export async function updateContactList(session: NostrSession, pubkeys: string[]): Promise<void> {
    const safeRelays = resolveRelays(session)
    const tags = pubkeys.map(pk => ['p', pk])
    const signed = await createSignedEvent(session, 3, '', tags)

    const pool = new SimplePool()
    try {
        await Promise.any(pool.publish(safeRelays, signed))
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Fetch kind-1 text notes tagged with a specific hashtag (#t filter).
 */
export async function fetchNostrHashtagPosts(
    tag: string,
    options?: { limit?: number; until?: number },
    relays?: string[]
): Promise<NostrEvent[]> {
    const safeRelays = resolveRelays(undefined, relays)
    const pool = new SimplePool()
    const filter: Filter = {
        kinds: [1],
        '#t': [tag.replace(/^#/, '')],
        limit: options?.limit ?? 50,
        ...(options?.until !== undefined ? { until: options.until } : {}),
    }
    try {
        const events = await pool.querySync(safeRelays, filter)
        return events
            .filter(e => verifyEvent(e as Parameters<typeof verifyEvent>[0]))
            .sort((a, b) => b.created_at - a.created_at) as NostrEvent[]
    } finally {
        pool.close(safeRelays)
    }
}

/**
 * Batch-fetch kind-0 (metadata) events for multiple pubkeys.
 * Returns a Map from pubkey to parsed profile data.
 * Events that fail verification or have unparseable content are skipped.
 */
// purplepag.es is a dedicated profile-indexing relay that aggregates kind-0
// events from across the network — always include it for profile fetches.
// Run through sanitizeRelays so it passes the same SSRF/wss guards as session relays.
const PROFILE_RELAYS = sanitizeRelays(['wss://purplepag.es'])

export async function fetchNostrProfiles(
    pubkeys: string[],
    relays?: string[]
): Promise<Map<string, NostrProfileEvent>> {
    if (pubkeys.length === 0) return new Map()
    const sessionRelays = resolveRelays(undefined, relays)
    const safeRelays = [...new Set([...sessionRelays, ...PROFILE_RELAYS])]
    const pool = new SimplePool()
    const profileMap = new Map<string, NostrProfileEvent>()
    try {
        const events = await pool.querySync(safeRelays, { kinds: [0], authors: pubkeys })
        for (const event of events) {
            if (!verifyEvent(event as Parameters<typeof verifyEvent>[0])) continue
            try {
                const parsed = JSON.parse(event.content) as Record<string, string>
                profileMap.set(event.pubkey, {
                    pubkey: event.pubkey,
                    name: parsed.name,
                    display_name: parsed.display_name,
                    about: parsed.about,
                    picture: parsed.picture?.startsWith('https://') ? parsed.picture : undefined,
                    website: parsed.website?.startsWith('https://') ? parsed.website : undefined,
                    nip05: parsed.nip05,
                    lud16: parsed.lud16,
                })
            } catch {
                profileMap.set(event.pubkey, { pubkey: event.pubkey })
            }
        }
    } finally {
        pool.close(safeRelays)
    }
    return profileMap
}

/** Engagement counts for a single Nostr event */
export interface EngagementCounts {
    likeCount: number
    repostCount: number
    replyCount: number
    /** The current user's kind-7 reaction event ID (only set when userPubkey matches) */
    likeUri?: string
    /** The current user's kind-6 repost event ID (only set when userPubkey matches) */
    repostUri?: string
}

/**
 * Batch-fetch engagement counts (likes, reposts, replies) for a set of event IDs.
 * Counts unique reactor pubkeys per event to avoid duplicates.
 * If userPubkey is provided, also checks whether the user has liked/reposted each event.
 */
export async function fetchEngagementCounts(
    eventIds: string[],
    options?: { userPubkey?: string; relays?: string[] }
): Promise<Map<string, EngagementCounts>> {
    if (eventIds.length === 0) return new Map()

    const safeRelays = resolveRelays(undefined, options?.relays)
    const pool = new SimplePool()
    const result = new Map<string, EngagementCounts>()

    for (const id of eventIds) {
        result.set(id, { likeCount: 0, repostCount: 0, replyCount: 0 })
    }

    const MAX_REACTIONS = 500

    try {
        const [reactionEvents, replyEvents] = await Promise.all([
            pool.querySync(safeRelays, {
                kinds: [6, 7],
                '#e': eventIds,
                limit: MAX_REACTIONS,
            }),
            pool.querySync(safeRelays, {
                kinds: [1],
                '#e': eventIds,
                limit: MAX_REACTIONS,
            }),
        ])

        // Track unique pubkeys per event for like/repost deduplication
        const likers = new Map<string, Set<string>>()
        const reposters = new Map<string, Set<string>>()
        const userLikeEventId = new Map<string, string>()
        const userRepostEventId = new Map<string, string>()

        for (const event of reactionEvents) {
            if (!verifyEvent(event as Parameters<typeof verifyEvent>[0])) continue
            const eTag = event.tags.find((t: string[]) => t[0] === 'e')
            if (!eTag?.[1] || !result.has(eTag[1])) continue
            const refId = eTag[1]

            if (event.kind === 7 && event.content === '+') {
                if (!likers.has(refId)) likers.set(refId, new Set())
                likers.get(refId)!.add(event.pubkey)
                if (options?.userPubkey === event.pubkey) {
                    userLikeEventId.set(refId, event.id)
                }
            } else if (event.kind === 6) {
                if (!reposters.has(refId)) reposters.set(refId, new Set())
                reposters.get(refId)!.add(event.pubkey)
                if (options?.userPubkey === event.pubkey) {
                    userRepostEventId.set(refId, event.id)
                }
            }
        }

        // Count direct replies per event (NIP-10: reply marker or last e-tag)
        const replies = new Map<string, Set<string>>()

        for (const event of replyEvents) {
            if (!verifyEvent(event as Parameters<typeof verifyEvent>[0])) continue
            const eTags = event.tags.filter((t: string[]) => t[0] === 'e')
            if (eTags.length === 0) continue

            const hasMarkers = eTags.some(
                (t: string[]) => t[3] === 'reply' || t[3] === 'root' || t[3] === 'mention'
            )

            let directParentId: string | undefined
            if (hasMarkers) {
                // NIP-10 positional markers: reply tag identifies the direct parent
                const replyTag = eTags.find((t: string[]) => t[3] === 'reply')
                directParentId = replyTag?.[1]
            } else {
                // No markers: last e-tag is the direct parent
                directParentId = eTags[eTags.length - 1][1]
            }

            if (directParentId && result.has(directParentId)) {
                if (!replies.has(directParentId)) replies.set(directParentId, new Set())
                replies.get(directParentId)!.add(event.id)
            }
        }

        // Populate final counts
        for (const [eventId, counts] of result) {
            counts.likeCount = likers.get(eventId)?.size ?? 0
            counts.repostCount = reposters.get(eventId)?.size ?? 0
            counts.replyCount = replies.get(eventId)?.size ?? 0
            const likeId = userLikeEventId.get(eventId)
            if (likeId) counts.likeUri = likeId
            const repostId = userRepostEventId.get(eventId)
            if (repostId) counts.repostUri = repostId
        }
    } finally {
        pool.close(safeRelays)
    }

    return result
}

/**
 * Fetch kind-1 text notes for a specific author.
 */
export async function fetchNostrAuthorPosts(
    pubkey: string,
    options?: { limit?: number; until?: number },
    relays?: string[]
): Promise<NostrEvent[]> {
    const safeRelays = resolveRelays(undefined, relays)
    const pool = new SimplePool()
    const filter: Filter = {
        kinds: [1],
        authors: [pubkey],
        limit: options?.limit ?? 50,
        ...(options?.until !== undefined ? { until: options.until } : {}),
    }
    try {
        const events = await pool.querySync(safeRelays, filter)
        return events
            .filter(e => verifyEvent(e as Parameters<typeof verifyEvent>[0]))
            .sort((a, b) => b.created_at - a.created_at) as NostrEvent[]
    } finally {
        pool.close(safeRelays)
    }
}
